import React, { useEffect, useState } from 'react';
import { Input } from 'antd';
import { Check, Lock, ShieldCheck } from 'lucide-react';
import { AdminButton, AdminCard, AdminPageHeader, AdminStatusPill } from '../../ui';
import toast from 'react-hot-toast';
import {
  enrollAuthenticator,
  verifyAuthenticator,
  getAuthenticatorStatus,
} from '../../../../api/admin/security.api';

// Nhóm khoá bí mật 4 ký tự cho dễ nhập tay vào app Authenticator.
const groupKey = (k: string) => (k.match(/.{1,4}/g) || []).join(' ');

const ManagementSecurity = () => {
  const [sharedKey, setSharedKey] = useState<string>('');
  const [loadingEnroll, setLoadingEnroll] = useState(false);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [done, setDone] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [checking, setChecking] = useState(true);
  const [alreadyEnrolled, setAlreadyEnrolled] = useState(false);
  const [reconfigure] = useState(false); // luôn false: đã chặn tự cài lại (server) — chỉ SuperAdmin reset

  useEffect(() => {
    getAuthenticatorStatus()
      .then((r) => setAlreadyEnrolled(!!r.enrolled))
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  const startEnroll = async () => {
    setLoadingEnroll(true);
    setDone(false);
    setRecoveryCodes([]);
    try {
      const res = await enrollAuthenticator();
      setSharedKey(res.sharedKey);
    } catch {
      toast.error('Không tạo được khoá. Bạn cần đăng nhập bằng tài khoản admin.');
    }
    setLoadingEnroll(false);
  };

  const doVerify = async () => {
    if (code.length !== 6) return;
    setVerifying(true);
    try {
      const res = await verifyAuthenticator(code);
      if (res.valid) {
        setDone(true);
        setRecoveryCodes(res.recoveryCodes || []);
        toast.success('Xác minh thành công — đã bật Authenticator!');
      } else {
        toast.error('Mã không đúng. Kiểm tra lại mã 6 số trên app.');
      }
    } catch {
      toast.error('Xác minh lỗi. Thử lại.');
    }
    setVerifying(false);
  };

  const copy = (text: string, label: string) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success(`Đã copy ${label}`),
      () => {}
    );
  };

  return (
    <div className="adm-page">
      <div style={{ maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 24 }}>
        <AdminPageHeader
          title="Bảo mật — Authenticator (2 lớp)"
          description="Cài đặt app Authenticator (Microsoft / Google Authenticator) để xác thực khi làm thao tác nhạy cảm như duyệt quyết toán thù lao. Cần dùng tài khoản admin."
        />

        {checking && <p className="adm-muted" style={{ margin: 0 }}>Đang kiểm tra trạng thái…</p>}

        {!checking && alreadyEnrolled && !reconfigure && !done && (
          <AdminCard>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <span className="adm-stat__icon adm-stat__icon--success" aria-hidden="true">
                <ShieldCheck size={20} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ fontSize: 16, lineHeight: '24px', fontWeight: 600, color: 'var(--adm-heading)' }}>
                    Authenticator đã được bật cho tài khoản này
                  </div>
                  <AdminStatusPill tone="success" icon={<Check size={12} />}>Đang bật</AdminStatusPill>
                </div>
                <p style={{ margin: '4px 0 0', fontSize: 14, lineHeight: '20px', color: 'var(--adm-text)' }}>
                  Khi làm thao tác nhạy cảm như đổi quyền hay duyệt rút tiền, bạn chỉ cần mở app và nhập mã 6 số.
                </p>
              </div>
            </div>
            <div className="adm-strip adm-strip--warning" role="status" style={{ borderRadius: 12 }}>
              <Lock size={20} className="adm-strip__icon" />
              <p style={{ margin: 0 }}>
                Vì lý do bảo mật, <b>không thể tự cài lại</b>. Nếu bạn <b>đổi hoặc mất điện thoại</b>, hãy nhờ một{' '}
                <b>SuperAdmin</b> vào <b>Phân quyền</b> rồi chọn <b>Reset Authenticator</b> cho tài khoản này (có ghi nhật ký),
                sau đó bạn cài lại 1 lần.
              </p>
            </div>
          </AdminCard>
        )}

        {!checking && (!alreadyEnrolled || reconfigure) && (
          <>
            {/* Bước 1: tạo khoá */}
            <AdminCard title="Bước 1 — Lấy khoá">
              <div>
                <AdminButton loading={loadingEnroll} onClick={startEnroll}>
                  {sharedKey ? 'Tạo lại khoá mới' : 'Bắt đầu cài đặt'}
                </AdminButton>
              </div>

              {sharedKey && (
                <div>
                  <div style={{ fontSize: 14, marginBottom: 4, color: 'var(--adm-text)' }}>
                    Mở app Authenticator → <b>Thêm tài khoản</b> → <b>Nhập khoá thủ công</b>, rồi nhập:
                  </div>
                  <ul style={{ fontSize: 14, margin: '0 0 12px', paddingLeft: 20, listStyle: 'disc', color: 'var(--adm-muted)' }}>
                    <li>Tên tài khoản: <b>Cab.vn</b> (hoặc email admin)</li>
                    <li>Loại: <b>Time-based</b> (theo thời gian)</li>
                  </ul>
                  <div
                    className="adm-panel"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, cursor: 'pointer' }}
                    onClick={() => copy(sharedKey, 'khoá')}
                    title="Bấm để copy"
                  >
                    <span className="font-mono" style={{ fontSize: 18, letterSpacing: '0.05em', color: 'var(--adm-heading)', wordBreak: 'break-all' }}>
                      {groupKey(sharedKey)}
                    </span>
                    <span className="adm-link" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Copy</span>
                  </div>
                </div>
              )}
            </AdminCard>

            {/* Bước 2: xác minh */}
            {sharedKey && !done && (
              <AdminCard title="Bước 2 — Nhập mã 6 số từ app để xác minh">
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onPressEnter={doVerify}
                    placeholder="123456"
                    inputMode="numeric"
                    style={{ maxWidth: 200, textAlign: 'center', letterSpacing: '0.3em', fontSize: 18 }}
                  />
                  <AdminButton loading={verifying} disabled={code.length !== 6} onClick={doVerify}>
                    Xác minh & bật
                  </AdminButton>
                </div>
              </AdminCard>
            )}
          </>
        )}

        {/* Xong + mã khôi phục */}
        {done && (
          <AdminCard>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="adm-stat__icon adm-stat__icon--success" aria-hidden="true">
                <ShieldCheck size={20} />
              </span>
              <div style={{ fontSize: 16, lineHeight: '24px', fontWeight: 600, color: 'var(--adm-success)' }}>
                Đã bật Authenticator cho tài khoản admin
              </div>
            </div>
            {recoveryCodes.length > 0 && (
              <div>
                <div style={{ fontSize: 14, marginBottom: 8, color: 'var(--adm-text)' }}>
                  <b>Mã khôi phục</b> — lưu lại nơi an toàn (dùng khi mất điện thoại). Chỉ hiện 1 lần:
                </div>
                <div className="adm-panel font-mono" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, fontSize: 14, marginBottom: 12 }}>
                  {recoveryCodes.map((c) => (
                    <div key={c} style={{ color: 'var(--adm-heading)' }}>
                      {c}
                    </div>
                  ))}
                </div>
                <AdminButton variant="soft" size={32} onClick={() => copy(recoveryCodes.join('\n'), 'mã khôi phục')}>
                  Copy tất cả
                </AdminButton>
              </div>
            )}
          </AdminCard>
        )}
      </div>
    </div>
  );
};

export default ManagementSecurity;
